import { useInvoiceDownloads } from "../features/invoices/useInvoiceDownloads";
import { getAnonymousInvoiceViewModel } from "./anonymousInvoiceViewModel";
import { useSiteConfig } from "../features/tenant/SiteConfigContext";
import { downloadInvoicePdf, downloadReceiptPdf } from "../lib/gboPdfDownloads";
import { normalizeInvoiceReferenceCode } from "../lib/lookupCode";
import { routes } from "../lib/route";
import { getApiFailureMessage, getApiFailureName, useGetInvoiceQuery } from "../store/gboApi";

export function useAnonymousInvoicePage(invoiceCode: string) {
  const siteConfig = useSiteConfig();
  const normalizedInvoiceCode = normalizeInvoiceReferenceCode(invoiceCode);
  const invoiceQuery = useGetInvoiceQuery({ invoiceCode: normalizedInvoiceCode });
  const { invoiceDownload, receiptDownloads, downloadInvoice, downloadReceipt } =
    useInvoiceDownloads({
      invoice: (id) => downloadInvoicePdf({ id, siteConfig }),
      receipt: (id) => downloadReceiptPdf({ id, siteConfig }),
    });

  if (invoiceQuery.isLoading || invoiceQuery.isFetching) return { status: "loading" as const };

  if (invoiceQuery.isError && getApiFailureName(invoiceQuery.error) === "NotFoundError") {
    return {
      status: "notFound" as const,
      message: getApiFailureMessage(invoiceQuery.error, "Invoice not found."),
      returnRoute: routes.search(),
    };
  }

  if (invoiceQuery.isError) {
    return {
      status: "error" as const,
      message: getApiFailureMessage(invoiceQuery.error, "Unable to load invoice."),
    };
  }

  if (!invoiceQuery.data) {
    return {
      status: "error" as const,
      message: "The invoice response could not be displayed.",
    };
  }

  const viewModel = getAnonymousInvoiceViewModel(invoiceQuery.data, siteConfig);

  return {
    status: "ready" as const,
    invoiceCode: normalizedInvoiceCode,
    invoiceDownload,
    receiptDownloads,
    viewModel,
    downloadInvoice: () => downloadInvoice(viewModel.document.id),
    downloadReceipt,
  };
}
